import React from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Heart, Play, Shuffle, Download } from 'lucide-react'
import { useMusic } from '../contexts/MusicContext'

const FavoritesPage = () => {
  const navigate = useNavigate()
  const { favorites, setPlaylist, setCurrentTrack, removeFavorite } = useMusic()

  const playAllFavorites = () => {
    if (favorites.length > 0) {
      setPlaylist(favorites)
      setCurrentTrack(favorites[0])
      navigate('/player')
    }
  }

  const shufflePlay = () => {
    if (favorites.length > 0) {
      const shuffled = [...favorites].sort(() => Math.random() - 0.5)
      setPlaylist(shuffled)
      setCurrentTrack(shuffled[0])
      navigate('/player')
    }
  }

  const playTrack = (track) => {
    setPlaylist(favorites)
    setCurrentTrack(track)
    navigate('/player')
  }

  const formatDuration = (duration) => {
    if (!duration) return '--:--'
    
    const minutes = Math.floor(duration / 60)
    const seconds = Math.floor(duration % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const getTotalDuration = () => {
    return favorites.reduce((total, track) => total + (track.duration || 0), 0)
  }

  return (
    <div className="min-h-screen">
      {/* 顶部导航栏 */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="sticky top-0 z-10 bg-white bg-opacity-80 backdrop-blur-sm border-b border-gray-200"
      >
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>返回</span>
            </motion.button>

            <h1 className="text-xl font-semibold text-gray-800">我的收藏</h1>

            <div className="w-16"></div> {/* 占位符保持平衡 */}
          </div>
        </div>
      </motion.header>

      {/* 主要内容 */}
      <div className="container mx-auto px-4 py-8">
        {favorites.length === 0 ? (
          // 空状态
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="text-6xl mb-4">💝</div>
            <h2 className="text-2xl font-semibold text-gray-700 mb-4">
              还没有收藏的音乐
            </h2>
            <p className="text-gray-500 mb-8">
              去发现一些你喜欢的音乐吧！
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/')}
              className="px-8 py-3 bg-gradient-to-r from-purple-500 to-blue-600 text-white rounded-full font-medium hover:from-purple-600 hover:to-blue-700 transition-all duration-200 shadow-lg"
            >
              开始探索音乐
            </motion.button>
          </motion.div>
        ) : (
          // 收藏列表
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* 收藏统计和操作 */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">
                    我的收藏音乐
                  </h2>
                  <p className="text-gray-500">
                    {favorites.length} 首歌曲 · 总时长 {formatDuration(getTotalDuration())}
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={shufflePlay}
                    className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full text-gray-700 transition-colors"
                  >
                    <Shuffle className="w-4 h-4" />
                    <span>随机播放</span>
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={playAllFavorites}
                    className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-purple-500 to-blue-600 text-white rounded-full font-medium hover:from-purple-600 hover:to-blue-700 transition-all duration-200"
                  >
                    <Play className="w-4 h-4" />
                    <span>播放全部</span>
                  </motion.button>
                </div>
              </div>
            </div>

            {/* 歌曲列表 */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="divide-y divide-gray-100">
                {favorites.map((track, index) => (
                  <motion.div
                    key={track.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="group flex items-center space-x-4 p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => playTrack(track)}
                  >
                    {/* 序号和播放按钮 */}
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                      <span className="text-sm text-gray-500 group-hover:hidden">
                        {index + 1}
                      </span>
                      <Play className="w-4 h-4 text-purple-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>

                    {/* 封面 */}
                    <div className="flex-shrink-0">
                      <img
                        src={track.artwork || '/default-cover.jpg'}
                        alt={track.title}
                        className="w-14 h-14 rounded-lg object-cover"
                      />
                    </div>

                    {/* 歌曲信息 */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 truncate">
                        {track.title}
                      </h3>
                      <p className="text-sm text-gray-600 truncate">
                        {track.artist} {track.album && `· ${track.album}`}
                      </p>
                      {track.source && (
                        <span className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full mt-1">
                          {track.source}
                        </span>
                      )}
                    </div>

                    {/* 时长 */}
                    <div className="flex-shrink-0 text-sm text-gray-500">
                      {formatDuration(track.duration)}
                    </div>

                    {/* 操作按钮 */}
                    <div className="flex-shrink-0 flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      {track.downloadUrl && (
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={(e) => {
                            e.stopPropagation()
                            window.open(track.downloadUrl, '_blank')
                          }}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                        >
                          <Download className="w-4 h-4" />
                        </motion.button>
                      )}

                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={(e) => {
                          e.stopPropagation()
                          removeFavorite(track.id)
                        }}
                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                      >
                        <Heart className="w-4 h-4" fill="currentColor" />
                      </motion.button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  )
}

export default FavoritesPage
